import { voucherApi } from "@/main/api";
import { withQueryparams } from "@/main/components/core/queryParams/queryparams";
import Button from "@/main/components/shared/button/button";
import ContainerScreen from "@/main/components/shared/containerScreen/containerLayout";
import { AreaButton } from "@/main/components/shared/styles";
import useElementSize from "@/main/hooks/useElementSize";
import { VoucherSearchGridRequest } from "@/main/models/request";
import { VoucherSearchGridItemResponse } from "@/main/models/response";
import ClaimsSourceSeal from "@/main/screens/order/refunds/components/claimsSourceSeal/claimsSourceSeal";
import FiltersRefunds from "@/main/screens/order/refunds/components/filtersRefunds/filtersRefunds";
import RefundsGrid from "@/main/screens/order/refunds/components/refundsGrid/refundsGrid";
import RefundsTotalCard from "@/main/screens/order/refunds/components/refundsTotalCard/refundsTotalCard";
import {
	buildRefundSearchRequest,
	EMPTY_REFUNDS_PAGE,
	isClaimsUnavailableError,
	ISearchRefundsForm,
	toRefundsPage,
} from "@/main/screens/order/refunds/refundsScreenUtils";
import { Controls } from "@/main/services/constants/constantsControl";
import { getTranslateByKey, useIsShowControlByElementId } from "@/main/services/utils";
import Alert from "@mui/material/Alert";
import { useCallback, useRef, useState } from "react";
import * as S from "./styles";

function RefundsScreen() {
	const [filterRequest, setFilterRequest] = useState<VoucherSearchGridRequest | null>(null);
	const [dataGrid, setDataGrid] = useState<VoucherSearchGridItemResponse[]>([]);
	const [totalAmount, setTotalAmount] = useState<number | null>(null);
	const [isLoadingTotal, setIsLoadingTotal] = useState(false);
	const [isClaimsUnavailable, setIsClaimsUnavailable] = useState(false);
	const [showFilters, setShowFilters] = useState(true);
	const showPrices = useIsShowControlByElementId(Controls.IdShowPrices);
	const filtersAreaRef = useRef<HTMLDivElement>(null);
	const filtersAreaSize = useElementSize(filtersAreaRef);

	const title = [getTranslateByKey("information.claim"), getTranslateByKey("screen.searchRefund")].join(" > ");

	const fetchRefunds = useCallback(async (request: VoucherSearchGridRequest) => {
		try {
			setIsLoadingTotal(true);
			const response = await voucherApi.getRefunds(request);
			const refunds = response.data.result ?? null;
			setTotalAmount(refunds?.totalAmount ?? null);
			return toRefundsPage(refunds, request);
		} catch (error) {
			setIsClaimsUnavailable(isClaimsUnavailableError(error));
			return EMPTY_REFUNDS_PAGE;
		} finally {
			setIsLoadingTotal(false);
		}
	}, []);

	function resetResults() {
		setDataGrid([]);
		setTotalAmount(null);
		setIsClaimsUnavailable(false);
	}

	function handleSubmit(formData: ISearchRefundsForm) {
		resetResults();
		setIsLoadingTotal(true);
		setShowFilters(false);
		setFilterRequest(buildRefundSearchRequest(formData));
	}

	function handleClear() {
		resetResults();
		setFilterRequest(null);
	}

	function handleFilters() {
		setShowFilters(!showFilters);
		window.scrollTo({ top: 0, behavior: "smooth" });
	}

	return (
		<ContainerScreen style={{ minHeight: "90%" }} title={title}>
			<S.Container>
				<S.SealArea $offsetRight={filtersAreaSize.width} $height={filtersAreaSize.height}>
					<ClaimsSourceSeal />
				</S.SealArea>

				<AreaButton ref={filtersAreaRef}>
					<Button id="id-refunds-btn-filters" onClick={handleFilters}>
						{getTranslateByKey("information.filters")}
					</Button>
				</AreaButton>

				{filterRequest && showPrices && (
					<RefundsTotalCard
						idVoucherRefundStatus={filterRequest.idVoucherRefundStatus}
						totalAmount={totalAmount}
						isLoading={isLoadingTotal}
					/>
				)}

				<FiltersRefunds showFilters={showFilters} onSubmit={handleSubmit} onClear={handleClear} />

				{isClaimsUnavailable ? (
					<Alert severity="warning" id="id-refunds-alert-claimsUnavailable">
						{getTranslateByKey("information.xnuupClaimsUnavailable")}
					</Alert>
				) : (
					<RefundsGrid
						filter={filterRequest}
						dataGrid={dataGrid}
						setDataGrid={setDataGrid}
						fetchRefunds={fetchRefunds}
						showPrices={showPrices}
					/>
				)}
			</S.Container>
		</ContainerScreen>
	);
}

export default withQueryparams(RefundsScreen);
